import Button from "@metacrm/metacrm-material-ui/dist/Button";
import { Box } from "@mui/material";
import { centerTruncate, getShortAddress } from "features/helpers/utils";
import useCopyToClipboard from "hooks/useCopyToClipboard";
import React, { useEffect, useCallback } from "react";
import { useOnboardingState } from "../OnboardingProvider";
import useTrackEvent from "../hook/useTrackEvent";
import { useIntl } from "react-intl";

export default function NoWorkspace({ prevStep }) {
  const { address, email, disconnect } = useOnboardingState();
  const copyFn = useCopyToClipboard(address || email);
  const { nextStep } = useOnboardingState();
  const { formatMessage } = useIntl();
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent("onboarding_no_workspace_view");
  }, [trackEvent]);

  const handleCreateWorkspace = useCallback(() => {
    trackEvent("onboarding_create_workspace_click");
    nextStep();
  }, [trackEvent, nextStep]);

  const handleDisconnect = useCallback(async () => {
    trackEvent("onboarding_switch_account_click");
    try {
      await disconnect();
      trackEvent("onboarding_account_switched");
    } catch (error) {
      trackEvent("onboarding_account_switch_error", {
        error_message: error.message,
      });
    }
  }, [disconnect, prevStep, trackEvent]);

  const handleCopyAddress = useCallback(() => {
    trackEvent("onboarding_copy_address_click");
    copyFn();
  }, [copyFn, trackEvent]);

  return (
    <Box position="relative" sx={{ maxHeight: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          height: "50vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          bgcolor: "#FAFAFD",
        }}
        pb="48px"
      >
        <Box sx={{ width: "400px", textAlign: "center" }}>
          <img
            src={require("assets/img/onboardingLogo.png")}
            style={{ width: 79, height: 79 }}
            alt="MetaCRM Logo"
          />
          <Box fontSize="24px" fontWeight={700} mt="8px" mb="4px">
            {formatMessage({ id: "global.workspace.create.title" })}
          </Box>
          <Box color="#777" fontSize="14px" mb="32px">
            {formatMessage({ id: "global.workspace.create.subtitle" })}
          </Box>
          <Button onClick={handleCreateWorkspace}>
            {formatMessage({ id: "global.btn.createWorkspace" })}
          </Button>
        </Box>
      </Box>
      <Box
        position="absolute"
        top="50%"
        left={"50%"}
        sx={{ transform: "translate(-50%, -50%)" }}
        color="#777"
        fontSize="14px"
      >
        {formatMessage({ id: "global.commonWord.or" })}
      </Box>
      <Box
        sx={{
          minHeight: "50vh",
          bgcolor: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
        pt="48px"
        pb="24px"
      >
        <Box fontSize="20px" fontWeight={700} mb="4px">
          {formatMessage({ id: "global.workspace.check.title" })}
        </Box>
        <Box fontSize="14px" mb="4px" color="#777">
          {formatMessage({ id: "global.workspace.check.subtitle" })}
        </Box>
        <Box fontSize="14px" color="#7B61FF" mb="24px">
          {email || getShortAddress(address)}
        </Box>
        <Box display="flex" alignItems={"stretch"} gap="12px" flexWrap={"wrap"}>
          <Box
            p="24px"
            width="280px"
            bgcolor="#FAFAFD"
            borderRadius="4px"
            m="0 auto"
            lineHeight="normal"
          >
            <Box color="#555" fontSize="14px" mb="12px" fontWeight={500}>
              {formatMessage({ id: "global.workspace.invite.title" })}
            </Box>
            <Box color="#777" fontSize="14px" fontWeight={400}>
              1.{" "}
              {formatMessage({
                id: "workspace.invitation.request.description1",
              })}
            </Box>
            <Box color="#777" fontSize="14px" fontWeight={400} mb="12px">
              2.{" "}
              {formatMessage({
                id: "workspace.invitation.request.description2",
              })}
            </Box>

            <Box mb="4px">
              {formatMessage({ id: "global.workspace.invite.accountInfo" })}
            </Box>
            <Box
              fontSize="12px"
              fontWeight={600}
              color="#7B61FF"
              display="flex"
              alignItems="center"
              gap="4px"
            >
              {email || getShortAddress(address)}
              <i
                className="meta-crm-icon-ic_copy font-size-16"
                style={{ cursor: "pointer", fontWeight: 300 }}
                onClick={handleCopyAddress}
              />
            </Box>
          </Box>

          <Box
            p="24px"
            width="280px"
            bgcolor="#FAFAFD"
            borderRadius="4px"
            m="0 auto"
            lineHeight="normal"
          >
            <Box color="#555" fontSize="14px" mb="12px" fontWeight={500}>
              {formatMessage({ id: "global.workspace.switchAccount.title" })}
            </Box>
            <Box color="#777" fontSize="14px" fontWeight={400} mb="40px">
              {formatMessage({ id: "global.workspace.switchAccount.subtitle" })}
            </Box>

            <Box
              fontSize="13px"
              fontWeight={700}
              color="#7B61FF"
              onClick={handleDisconnect}
              sx={{ cursor: "pointer" }}
            >
              {`${formatMessage({ id: "global.btn.switchAccount" })} ->`}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
